export const ADMIN_PROFILES_PAGE_SIZE = 25;

const USER_SORT_FIELD_MAP = {
    id: 'u.id',
    nickname: 'up.nick',
    registrationDate: 'u.created_at',
    status: 'u.has_blocked',
};

const ORGANIZER_SORT_FIELD_MAP = {
    registration_date: 'u.created_at',
    company_name: 'op.company_name',
};

const SEX_LABELS = {
    MALE: 'mężczyzna',
    FEMALE: 'kobieta',
    OTHER: 'inna',
    UNSPECIFIED: '-',
};

const ADMIN_ROLES = new Set(['SYSTEM_ADMIN', 'EVENT_ADMIN']);

const toDateOnly = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toISOString().slice(0, 10);
};

const normalizeText = (value, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text.length > 0 ? text : fallback;
};

const getFullName = (profile) => {
    const first = profile?.name?.trim?.() || '';
    const last = profile?.surname?.trim?.() || '';
    const full = [first, last].filter(Boolean).join(' ').trim();
    return full || '-';
};

const mapSexLabel = (sex, optionalSexName) => {
    if (sex === 'OTHER' && optionalSexName) return optionalSexName;
    return SEX_LABELS[sex] || '-';
};

const hasOrganizerProfile = (entry) => {
    const op = entry?.organiserProfileResponseDto;
    return Boolean(op && (op.id || op.companyName || op.contactEmail));
};

const getUserStatus = (user) => {
    if (user?.hasBlocked) return 'blocked';
    if (user?.forDeleting === true) return 'forDeleting';

    const activationFlag = user?.idActivated ?? user?.isActivated;
    if (activationFlag === false) return 'pending';

    return 'active';
};

const getOrganizerStatus = (entry) => {
    const user = entry?.userResponseFullDto;
    if (user?.hasBlocked) return 'blocked';
    return user?.isActivated === false ? 'pending' : 'accepted';
};

const formatOrganizerAddress = (organiserProfile) => {
    const street = organiserProfile?.street?.trim?.() || '';
    const postalCode = organiserProfile?.postalCode?.trim?.() || '';
    const city = organiserProfile?.city?.trim?.() || '';
    const line2 = [postalCode, city].filter(Boolean).join(' ').trim();

    if (street && line2) return `${street}, ${line2}`;
    if (street) return street;
    if (line2) return line2;
    return '-';
};

const extractSortableValue = (row, sortBy) => {
    if (!sortBy) return null;
    if (sortBy === 'reportDate') {
        return row?.createdAt ?? row?.reportDate;
    }
    return row?.[sortBy];
};

export const sortRowsLocally = (rows, sortBy, sortDirection) => {
    if (!sortBy || !sortDirection) return rows;

    const sorted = [...rows];
    sorted.sort((a, b) => {
        const aVal = extractSortableValue(a, sortBy);
        const bVal = extractSortableValue(b, sortBy);

        if (aVal === bVal) return 0;
        if (aVal === undefined || aVal === null) return 1;
        if (bVal === undefined || bVal === null) return -1;

        if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sortDirection === 'ASC' ? aVal - bVal : bVal - aVal;
        }

        const aDate = Date.parse(aVal);
        const bDate = Date.parse(bVal);
        if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
            return sortDirection === 'ASC' ? aDate - bDate : bDate - aDate;
        }

        const compare = String(aVal).localeCompare(String(bVal), 'pl', { sensitivity: 'base' });
        return sortDirection === 'ASC' ? compare : -compare;
    });

    return sorted;
};

export const estimatePagesFromCurrentBatch = (currentPage, batchLength, pageSize = ADMIN_PROFILES_PAGE_SIZE) => {
    if (batchLength < pageSize) return Math.max(1, currentPage);
    return Math.max(1, currentPage + 1);
};

export const extractProfilesEntries = (response) => {
    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray(response?.accountsWrapperResponseDtoList)) {
        return response.accountsWrapperResponseDtoList;
    }

    return [];
};

export const extractProfilesNumberOfPages = (response) => {
    const parsed = Number(response?.numberOfPages);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const extractProfilesTotalElements = (response) => {
    const candidates = [
        response?.totalElements,
        response?.totalResults,
        response?.totalCount,
        response?.count,
    ];

    const parsed = candidates
        .map((value) => Number(value))
        .find((value) => Number.isFinite(value) && value >= 0);

    return parsed ?? null;
};

export const buildProfilesQuery = ({
    pageNumber = 1,
    pageSize = ADMIN_PROFILES_PAGE_SIZE,
    searchText = '',
    sortBy = null,
    sortDirection = null,
    tableType,
    pageTab,
    forceBlockedOnly,
}) => {
    const roleScope = getRoleScopeForQuery(tableType, pageTab);
    const blockedOnly = getBlockedOnlyForQuery(pageTab, forceBlockedOnly);

    const params = {
        pageNumber,
        pageSize,
        roleScope,
        blockedOnly,
    };

    if (searchText) {
        params.searchText = searchText;
    }

    const mappedSortField = mapSortFieldForTable(tableType, sortBy);
    if (mappedSortField && sortDirection) {
        params.sortField = mappedSortField;
        params.sortOrder = sortDirection;
    }

    return params;
};

const getRoleScopeForQuery = (tableType, pageTab) => {
    if (tableType === 'organizers') {
        return 'EVENT_ADMIN';
    }

    if (tableType === 'users') {
        if (pageTab === 'admins') {
            return 'SYSTEM_ADMIN';
        }
        if (pageTab === 'users') {
            return 'USER';
        }
        if (pageTab === 'blocked') {
            return 'ALL_USERS';
        }
    }

    return 'ALL_USERS';
};

const getBlockedOnlyForQuery = (pageTab, forceBlockedOnly) => {
    if (typeof forceBlockedOnly === 'boolean') {
        return forceBlockedOnly;
    }

    return pageTab === 'blocked';
};

const mapSortFieldForTable = (tableType, sortBy) => {
    if (!sortBy) return null;
    const map = tableType === 'organizers' ? ORGANIZER_SORT_FIELD_MAP : USER_SORT_FIELD_MAP;
    return map[sortBy] || null;
};

export const normalizeProfilesToUsers = (entries = []) => {
    return entries
        .filter(Boolean)
        .map((entry, index) => {
            const user = entry.userResponseFullDto || {};
            const profile = entry.profileResponseDto || {};
            const op = entry.organiserProfileResponseDto || {};
            const hasOrganizer = hasOrganizerProfile(entry);
            const status = getUserStatus(user);
            const role = user?.hasRole || 'USER';

            const rawCreatedPacks = entry.createdPacks
                ?? entry.foundedBunches
                ?? entry.bunchesCount
                ?? user.createdPacks
                ?? user.foundedBunches
                ?? user.bunchesCount
                ?? profile.createdPacks
                ?? profile.foundedBunches
                ?? profile.bunchesCount;
            const createdPacks = Number.isFinite(Number(rawCreatedPacks)) && rawCreatedPacks !== null && rawCreatedPacks !== ''
                ? Number(rawCreatedPacks)
                : '-';

            const rawLikedEvents = entry.likedEvents
                ?? entry.favoriteEventsCount
                ?? entry.favoriteEvents
                ?? entry.favoritesCount
                ?? entry.favoriteEventsCounts
                ?? user.likedEvents
                ?? user.favoriteEventsCount
                ?? user.favoriteEvents
                ?? user.favoritesCount
                ?? profile.likedEvents
                ?? profile.favoriteEventsCount
                ?? profile.favoriteEvents
                ?? profile.favoritesCount;
            const likedEvents = Number.isFinite(Number(rawLikedEvents)) && rawLikedEvents !== null && rawLikedEvents !== ''
                ? Number(rawLikedEvents)
                : '-';

            return {
                id: user.id || profile.uuid || `missing-user-id-${index}`,
                nickname: normalizeText(profile.nick, '-'),
                fullName: getFullName(profile),
                gender: mapSexLabel(profile.sex, profile.optionalSexName),
                email: normalizeText(user.email || profile.email, '-'),
                phone: normalizeText(user.phoneNumber, '-'),
                registrationDate: toDateOnly(user.createdAt),
                lastLogin: '-',
                profileImageHashedId: normalizeText(profile.profileImageHashedId, ''),
                status,
                createdPacks,
                likedEvents,
                description: profile.description || '',
                role: ADMIN_ROLES.has(role)
                    ? 'admin'
                    : hasOrganizer
                        ? 'organizer'
                        : 'user',
                backendRole: role,
                hasOrganizerProfile: hasOrganizer,
                organiserProfileId: op.id || null,
                organizerProfileId: op.id || null,
                isActivated: (user.idActivated ?? user.isActivated) !== false,
                _rawProfileEntry: entry,
            };
        });
};

export const normalizeProfilesToOrganizers = (entries = []) => {
    return entries
        .filter((entry) => Boolean(entry) && hasOrganizerProfile(entry))
        .map((entry) => {
            const user = entry.userResponseFullDto || {};
            const organiserProfile = entry.organiserProfileResponseDto || {};
            const status = getOrganizerStatus(entry);

            return {
                id: organiserProfile.id || user.id,
                ownerId: user.id || organiserProfile.ownerId || organiserProfile.userId || null,
                userId: user.id || organiserProfile.userId || null,
                organiserProfileId: organiserProfile.id || null,
                organizerProfileId: organiserProfile.id || null,
                registrationDate: toDateOnly(user.createdAt),
                companyName: normalizeText(organiserProfile.companyName, '-'),
                nip: normalizeText(organiserProfile.nip, '-'),
                address: formatOrganizerAddress(organiserProfile),
                email: normalizeText(organiserProfile.contactEmail || user.email, '-'),
                phone: normalizeText(organiserProfile.contactPhone || user.phoneNumber, '-'),
                eventsCount: '-',
                status,
                pendingType: status === 'pending' ? 'new_profile' : '',
                isActivated: user.isActivated ?? organiserProfile.isActivated,
                isEmailConfirmed: organiserProfile.isEmailConfirmed ?? user.isEmailConfirmed,
                hasPendingChange: organiserProfile.hasPendingChange === true,
                pendingChangeRequestedAt: organiserProfile.pendingChangeRequestedAt ?? null,
                pendingRequestedAt: organiserProfile.pendingRequestedAt ?? organiserProfile.pendingChangeRequestedAt ?? null,
                isBlocked: Boolean(user.hasBlocked),
                description: organiserProfile.description || '',
                facebookUrl: organiserProfile.socialLink || '',
                websiteUrl: organiserProfile.siteLink || '',
                changedFields: {},
                isActive: (user.isActivated ?? organiserProfile.isActivated) !== false,
                _rawProfileEntry: entry,
            };
        });
};

export const filterUsersForTab = (users, pageTab, filters = {}) => {
    let filtered = [...users];

    filtered = filtered.filter((user) => {
        if (pageTab === 'admins') {
            return ADMIN_ROLES.has(user.backendRole);
        }
        if (pageTab === 'blocked') {
            return user.status === 'blocked';
        }
        if (pageTab === 'users') {
            return !ADMIN_ROLES.has(user.backendRole) && user.status !== 'blocked';
        }
        return true;
    });

    if (filters.dateFrom) {
        const from = new Date(filters.dateFrom);
        filtered = filtered.filter((user) => new Date(user.registrationDate) >= from);
    }
    if (filters.dateTo) {
        const to = new Date(filters.dateTo);
        to.setHours(23, 59, 59, 999);
        filtered = filtered.filter((user) => new Date(user.registrationDate) <= to);
    }
    if (filters.gender) {
        filtered = filtered.filter((user) => user.gender === filters.gender);
    }
    if (filters.status) {
        filtered = filtered.filter((user) => user.status === filters.status);
    }

    return filtered;
};

export const filterOrganizersForTab = (organizers, pageTab, filters = {}) => {
    let filtered = [...organizers];

    filtered = filtered.filter((org) => {
        if (pageTab === 'blocked') return org.status === 'blocked';
        if (pageTab === 'pending') return org.status === 'pending';
        if (pageTab === 'accepted') return org.status === 'accepted';
        return true;
    });

    if (filters.dateFrom) {
        const from = new Date(filters.dateFrom);
        filtered = filtered.filter((org) => new Date(org.registrationDate) >= from);
    }
    if (filters.dateTo) {
        const to = new Date(filters.dateTo);
        to.setHours(23, 59, 59, 999);
        filtered = filtered.filter((org) => new Date(org.registrationDate) <= to);
    }
    if (filters.status) {
        filtered = filtered.filter((org) => org.status === filters.status);
    }

    return filtered;
};

export const getOrganizerTabCountsFromRows = (organizers) => ({
    accepted: organizers.filter((org) => org.status === 'accepted').length,
    pending: organizers.filter((org) => org.status === 'pending').length,
    blocked: organizers.filter((org) => org.status === 'blocked').length,
});

export const getUserTabCountsFromRows = (users) => ({
    users: users.filter((user) => !ADMIN_ROLES.has(user.backendRole) && user.status !== 'blocked').length,
    admins: users.filter((user) => ADMIN_ROLES.has(user.backendRole)).length,
    blocked: users.filter((user) => user.status === 'blocked').length,
});
